import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Slide from '@mui/material/Slide';
import { useTheme } from '@mui/material/styles';
import { toast, getToastActionCallback, clearToastActionCallback } from './toastActions';
import { ToastItem, ToastSeverity, ToastState, ToastAnchorOrigin } from './toastReducer';
import tokens from '@/theme/tokens';
import radii from '@/theme/radii';

const SEVERITY_ICONS: Record<ToastSeverity, React.ElementType> = {
  success: CheckCircleOutlineIcon,
  error: ErrorOutlineIcon,
  warning: WarningAmberIcon,
  info: InfoOutlinedIcon,
};

const containerSx = (anchor: ToastAnchorOrigin) => {
  const sx: any = {
    position: 'fixed',
    zIndex: (t: any) => t.zIndex.snackbar,
    display: 'flex',
    flexDirection: anchor.vertical === 'top' ? 'column' : 'column-reverse',
    gap: 1,
    p: 2,
    pointerEvents: 'none',
  };
  sx[anchor.vertical] = 0;
  if (anchor.horizontal === 'left') sx.left = 0;
  else if (anchor.horizontal === 'right') sx.right = 0;
  else {
    sx.left = '50%';
    sx.transform = 'translateX(-50%)';
  }
  return sx;
};

const groupKey = (a: ToastAnchorOrigin) => `${a.vertical}-${a.horizontal}`;

const ToastRow: React.FC<{ item: ToastItem }> = ({ item }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const paletteColor = theme.palette[item.severity];
  const color = paletteColor.main;
  const SeverityIcon = SEVERITY_ICONS[item.severity];
  const filled = item.severity === 'error' || item.severity === 'warning';
  const cardBg = filled ? color : theme.palette.background.paper;
  const textColor =
    item.severity === 'warning'
      ? theme.palette.common.black
      : filled
      ? paletteColor.contrastText
      : theme.palette.text.primary;
  const iconStripBg =
    item.severity === 'warning'
      ? tokens.color.toastWarningStrip
      : filled
      ? tokens.color.toastFilledStrip
      : color + '33';
  const iconColor =
    item.severity === 'warning'
      ? theme.palette.common.black
      : filled
      ? paletteColor.contrastText
      : color;

  useEffect(() => {
    if (item.hideDuration == null) return;
    const t = setTimeout(() => {
      clearToastActionCallback(item.id);
      dispatch(toast.hide(item.id));
    }, item.hideDuration);
    return () => clearTimeout(t);
  }, [item.id, item.hideDuration, dispatch]);

  const handleClose = () => {
    clearToastActionCallback(item.id);
    dispatch(toast.hide(item.id));
  };

  const handleAction = () => {
    const cb = getToastActionCallback(item.id);
    if (cb) cb();
    clearToastActionCallback(item.id);
    dispatch(toast.hide(item.id));
  };

  return (
    <Slide in direction={item.anchorOrigin.vertical === 'top' ? 'down' : 'up'}>
      <Paper
        elevation={8}
        sx={{
          borderRadius: `${radii.xl}px`,
          display: 'flex',
          alignItems: 'flex-start',
          minWidth: 320,
          maxWidth: 400,
          minHeight: 64,
          pointerEvents: 'auto',
          overflow: 'hidden',
          bgcolor: cardBg,
          color: textColor,
          border: filled ? 'none' : `2px solid ${color}`,
          boxShadow: filled
            ? `${tokens.shadow.toastCard} ${color}59`
            : `${tokens.shadow.toastCard} ${color}40`,
        }}
      >
        <Box
          sx={{
            width: 60,
            alignSelf: 'stretch',
            bgcolor: iconStripBg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <SeverityIcon sx={{ color: iconColor, fontSize: tokens.fontSize.toastSeverityIcon }} />
        </Box>

        <Box
          sx={{
            flex: 1,
            p: 2,
            alignSelf: 'stretch',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <Typography
            variant="body1"
            sx={{
              fontWeight: 500,
              color: textColor,
              lineHeight: 1.4
            }}>
            {item.message}
          </Typography>
          {item.actionLabel && (
            <Link
              component="button"
              variant="body2"
              onClick={handleAction}
              sx={{
                color: theme.palette.info.main,
                fontWeight: 700,
                mt: 1,
                textAlign: 'left',
                display: 'block',
                textDecoration: 'underline',
              }}
            >
              {item.actionLabel}
            </Link>
          )}
        </Box>

        <IconButton
          size="small"
          onClick={handleClose}
          sx={{ alignSelf: 'flex-start', color: textColor, m: 0.5 }}
        >
          <CloseIcon sx={{ fontSize: tokens.fontSize.toastCloseIcon }} />
        </IconButton>
      </Paper>
    </Slide>
  );
};

const Toast: React.FC = () => {
  const toasts = useSelector((state: any) => (state.toast as ToastState).toasts);

  const groups = new Map<string, { anchor: ToastAnchorOrigin; items: ToastItem[] }>();
  toasts.forEach((t) => {
    const key = groupKey(t.anchorOrigin);
    if (!groups.has(key)) groups.set(key, { anchor: t.anchorOrigin, items: [] });
    groups.get(key)!.items.push(t);
  });

  return (
    <>
      {Array.from(groups.entries()).map(([key, { anchor, items }]) => (
        <Box key={key} sx={containerSx(anchor)}>
          {items.map((item) => (
            <ToastRow key={item.id} item={item} />
          ))}
        </Box>
      ))}
    </>
  );
};

export default Toast;
